import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Add, Category, Edit, ExpandMore } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import GRImg from '../../../../../components/GRImg/GRImg'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import {
  useGetInternalCategoryFeaturesQuery,
  useSaveInternalCategoryFeatureOrderMutation,
  useToggleInternalFeatureActiveMutation,
} from '../../../../api/categories'
import { LanguageMaps } from '../../../../api/language'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { InternalCategoryFeature } from '../../../../types/InternalCategory'
import { InternalCategoryRecord } from '../../types/Categories'
import { CategoryFeaturesTable } from '../CategoryFeaturesTable/CategoryFeaturesTable'
import {
  applyFeatureOrder,
  getFeatureCounts,
  getMisorderedFeatures,
  isFeatureActiveStored,
  toFeatureActiveBody,
  toFeatureOrder,
} from '../helpers/categoryHelpers'
import { featureEditPath } from '../helpers/featureHelpers'

const headerIconSize = 32

type Named = { id: string; name: string }

type CategorySectionProps = {
  category: InternalCategoryRecord
  languageMaps?: LanguageMaps
  stores?: Named[]
  markets?: Named[]
  expanded: boolean
  onExpandedChange: (categoryId: string, expanded: boolean) => void
  onEdit: (category: InternalCategoryRecord) => void
  onOrderChanged: (categoryId: string, changed: boolean) => void
}

export const CategorySection: FC<CategorySectionProps> = ({ category, languageMaps, stores, markets, expanded, onExpandedChange, onEdit, onOrderChanged }) => {
  const { t } = useTranslation()
  const { run, notifyError, notifySuccess } = useMutationFeedback()
  const featuresQuery = useGetInternalCategoryFeaturesQuery({ categoryId: category.id })
  const [toggleActive, { isLoading: isToggling }] = useToggleInternalFeatureActiveMutation()
  const [saveOrder, { isLoading: isSavingOrder }] = useSaveInternalCategoryFeatureOrderMutation()
  const [reorderedIds, setReorderedIds] = useState<string[]>()
  const [confirmData, setConfirmData] = useState<ConfirmDialogData<InternalCategoryFeature>>()

  const features = useMemo(() => applyFeatureOrder(featuresQuery.data ?? [], reorderedIds), [featuresQuery.data, reorderedIds])
  const counts = useMemo(() => getFeatureCounts(featuresQuery.data ?? []), [featuresQuery.data])

  useEffect(() => {
    onOrderChanged(category.id, !!reorderedIds)
  }, [category.id, onOrderChanged, reorderedIds])

  const askToggle = useCallback(
    (feature: InternalCategoryFeature) =>
      setConfirmData({
        title: t(feature.active ? 'internal-analysts:categories_deactivate_feature' : 'internal-analysts:categories_activate_feature'),
        confirmText: t(feature.active ? 'internal-analysts:categories_deactivate_feature_confirm' : 'internal-analysts:categories_activate_feature_confirm', {
          name: feature.name,
        }),
        actionText: t(feature.active ? 'internal-analysts:categories_deactivate' : 'internal-analysts:categories_activate'),
        destructiveAction: !!feature.active,
        data: feature,
      }),
    [t]
  )

  const handleConfirm = async (confirmed?: ConfirmDialogData<InternalCategoryFeature>) => {
    setConfirmData(undefined)
    const feature = confirmed?.data
    if (!feature || !languageMaps) return
    const body = toFeatureActiveBody(feature, languageMaps)
    const toggled = await run(toggleActive(body).unwrap(), { error: t('internal-analysts:categories_feature_status_failed', { name: feature.name }) })
    if (!toggled.ok) return
    if (!isFeatureActiveStored(toggled.result.stored, body)) {
      notifyError(t('internal-analysts:categories_feature_status_not_stored', { name: feature.name }))
      return
    }
    notifySuccess(
      t(body.active ? 'internal-analysts:categories_feature_activated' : 'internal-analysts:categories_feature_deactivated', { name: feature.name })
    )
  }

  const handleSaveOrder = async () => {
    const order = toFeatureOrder(features)
    const saved = await run(saveOrder({ categoryId: category.id, order }).unwrap(), {
      error: t('internal-analysts:categories_order_save_failed', { category: category.name }),
    })
    if (!saved.ok) return
    const misordered = getMisorderedFeatures(order, saved.result.stored)
    if (misordered.length) {
      notifyError(t('internal-analysts:categories_order_not_stored', { category: category.name, features: misordered.join(', ') }))
      return
    }
    setReorderedIds(undefined)
    notifySuccess(t('internal-analysts:categories_order_saved', { category: category.name }))
  }

  const isBusy = isToggling || isSavingOrder || featuresQuery.isFetching

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => onExpandedChange(category.id, isExpanded)} TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<ExpandMore />} aria-controls={`category-${category.id}-features`} id={`category-${category.id}-header`}>
        <Stack direction="row" alignItems="center" spacing={2} flexGrow={1} mr={2}>
          <Box
            width={headerIconSize}
            height={headerIconSize}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            sx={{ '& img': { width: headerIconSize, height: headerIconSize, objectFit: 'contain', display: 'block' } }}
          >
            {category.icon ? <GRImg alt="" src={category.icon.url} /> : <Category color="disabled" />}
          </Box>
          <Typography fontWeight="bold">{category.name}</Typography>
          {!category.active && <Chip size="small" label={t('internal-analysts:categories_inactive')} />}
          {reorderedIds && <Chip size="small" color="warning" label={t('internal-analysts:categories_unsaved_order')} />}
          <Box flexGrow={1} />
          {featuresQuery.isLoading ? (
            <Skeleton width={160} />
          ) : featuresQuery.isError ? (
            <Typography variant="body2" color="error.main">
              {t('internal-analysts:categories_features_load_failed')}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('internal-analysts:categories_feature_counts', { active: counts.active, total: counts.total })}
            </Typography>
          )}
          <Tooltip title={t('internal-analysts:categories_edit')}>
            <IconButton
              size="small"
              color="primary"
              aria-label={t('internal-analysts:categories_edit_named', { name: category.name })}
              onClick={(event) => {
                event.stopPropagation()
                onEdit(category)
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </AccordionSummary>
      <AccordionDetails id={`category-${category.id}-features`}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={2} mb={2} flexWrap="wrap">
          {reorderedIds && (
            <Alert severity="info" sx={{ flexGrow: 1 }}>
              {t('internal-analysts:categories_order_changed')}
            </Alert>
          )}
          {reorderedIds && (
            <>
              <Button onClick={() => setReorderedIds(undefined)} disabled={isSavingOrder}>
                {t('internal-analysts:categories_reset_order')}
              </Button>
              <Button variant="contained" onClick={handleSaveOrder} disabled={isBusy}>
                {t('internal-analysts:categories_save_order')}
              </Button>
            </>
          )}
          <Button variant="outlined" startIcon={<Add />} component={Link} to={featureEditPath(category.id)}>
            {t('internal-analysts:categories_add_feature')}
          </Button>
        </Stack>
        <QueryStateBoundary
          isLoading={featuresQuery.isLoading}
          isError={featuresQuery.isError}
          errorText={t('internal-analysts:categories_features_load_failed')}
          skeletonHeight={240}
        >
          <CategoryFeaturesTable
            features={features}
            stores={stores}
            markets={markets}
            canToggle={!!languageMaps && !isBusy}
            sortable={!isBusy}
            onToggleActive={askToggle}
            onReorder={setReorderedIds}
          />
        </QueryStateBoundary>
      </AccordionDetails>
      <ConfirmDialog open={!!confirmData} confirmDialogData={confirmData} onClose={handleConfirm} />
    </Accordion>
  )
}
